// The event timeline is the event's spine (design/README.md, agreed 2026-10-16; it replaces the alarm chip row).
//   lanes    one per camera involved (alarm order), then an AI row and an Operator row; site time, anchored to the
//            first alarm as before; playhead; click a lane or the ruler to review archive there.
//   markers  alarms (analytic icon, burst ×N, unreviewed / reviewed / dismissed, "new" pulse for alarms that came in
//            while the event was open, guard tour, the agent's evidence frames); watch hits on the hit camera's lane;
//            AI assessment / watch set / escalate / auto-resolve on the AI row; calls, dispatch, notes, bookmarks and
//            park / raise on the Operator row.
//   alarms   click loads the clip at its time and marks it reviewed (as the chips did); the hover / focus card shows
//            the snapshot, site time, camera, analytic, burst, and Split / Dismiss.
//   dense    nearby markers in a lane cluster into a count bubble; clicking it zooms in on them. Zoom with − / + or
//            Ctrl/⌘-wheel; "Fit all" zooms back out; the lanes scroll sideways when zoomed.
//   deter    Phase 5: talk-downs and relay actions on the Operator row, or the AI row when the agent did them (and the
//            agent's would-have device calls in shadow); the hover card says what was played or which relay ran how
//            long, and plays the stored audio. Customer notifications are markers too.
//   footage  each camera lane shows what NX recorded over the event's span (a solid bar; a dashed line where nothing
//            was recorded), refreshed once a minute while the event is open; the stretch still being recorded runs on to
//            now between refreshes (footage.ts withRunning). Hovering a camera lane shows a small frame
//            from that moment (scrub preview) before you click to review there.
//   keys     markers are buttons (one tab stop per lane); ← → move along a lane, ↑ ↓ to the next lane, Enter loads.
//            J (console hotkey) steps to the next unreviewed alarm and moves focus here when focus was on the page.

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { api, type EventAction, type EventAlarm, type Watch } from '../api.ts';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { useNow } from '../hooks.ts';
import { recordedAt, withRunning, type Periods } from './footage.ts';
import { fmtTime, tzAbbr } from '../time.ts';
import { callWords, camList, hitOutcome, pct as pctOf, toolResultLine, verdictCls, verdictLabel, watchWhat } from './ai.ts';
import { AudioButton } from './DeterDrawers.tsx';
import { functionIcon } from './devices.ts';
import type { Ctl } from './ctl.ts';
import { GuardTourTag, isGuardTour } from './guardTour.tsx';
import { actorName, alarmMs, camShort, human, ms, plural, str } from './util.ts';
import { selectedAlarm, vtime } from './view.ts';

const CLUSTER_PX = 15;
/** How long the pointer rests before a scrub preview is fetched; previews are fetched per whole second (and cached). */
const PREVIEW_DELAY_MS = 120;

/** Recorded periods per camera over the event's span, asked again once a minute while the event is open. */
function useFootage(camIds: string[], a: number, b: number, now: number, open: boolean): Map<string, Periods | null> {
  const [got, setGot] = useState<{ map: Map<string, Periods | null>; asOf: number }>(() => ({ map: new Map(), asOf: 0 }));
  const key = camIds.join(',');
  const from = Math.floor(a / 60_000) * 60_000;
  const to = Math.ceil(b / 60_000) * 60_000;
  // The span's rounded end stays put until a minute after the last alarm, so an open event also asks on the minute.
  const tick = open ? Math.floor(now / 60_000) : 0;
  useEffect(() => {
    const end = Math.min(to, Date.now());
    if (!camIds.length || end <= from) return;
    const ac = new AbortController();
    const f = new Date(from).toISOString();
    const t = new Date(end).toISOString();
    void Promise.all(
      camIds.map((id) =>
        api.footage(id, f, t, ac.signal).then(
          (r) => [id, r.periods ? r.periods.map((p) => ({ start: Date.parse(p.start), end: Date.parse(p.end) })) : null] as const,
          () => [id, null] as const,
        ),
      ),
    ).then((entries) => {
      if (!ac.signal.aborted) setGot({ map: new Map(entries), asOf: end });
    });
    return () => ac.abort();
  }, [key, from, to, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(
    () => (open ? new Map([...got.map].map(([id, p]) => [id, p && withRunning(p, got.asOf, now)] as const)) : got.map),
    [got, open, now],
  );
}
const MIN_SPAN_MS = 30_000;
const TICK_STEPS = [1, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400].map((s) => s * 1000);

type MkKind = 'alarm' | 'hit' | 'watch' | 'assess' | 'escalate' | 'resolve' | 'op' | 'deter';

interface Mk {
  key: string;
  t: number;
  kind: MkKind;
  icon: IconName;
  cls: string;
  label: string;
  alarm?: EventAlarm;
  /** A-number of the alarm (1-based, oldest first). */
  n?: number;
  watch?: Watch;
  /** Phase 5: the talk-down or relay action behind a 'deter' marker. */
  action?: EventAction;
  activate: () => void;
}

interface Lane {
  key: string;
  kind: 'cam' | 'ai' | 'op';
  label: string;
  sub: string;
  camId: string | null;
  keyNo: number | null;
  marks: Mk[];
}

type Item = { k: 'mk'; key: string; t: number; mk: Mk } | { k: 'cl'; key: string; t: number; t0: number; t1: number; marks: Mk[] };

export function analyticIcon(a: string | null | undefined): IconName {
  const s = (a ?? '').toLowerCase();
  if (/vehicle|car\b|truck|lpr|plate|parking/.test(s)) return 'car';
  if (/person|human|people|loiter|intru|face|crowd|trespass|tailgat/.test(s)) return 'user';
  if (/line|cross|fence|perimeter|tripwire|virtual/.test(s)) return 'line';
  if (/motion/.test(s)) return 'motion';
  if (/fire|smoke|heat/.test(s)) return 'bolt';
  return 'alert';
}

function alarmStatusWord(a: EventAlarm): string {
  return a.reviewStatus === 'new' ? 'unreviewed' : a.reviewStatus;
}

/** Media id at the end of a snapshot URL (/api/media/:id). */
function citedAlarmIds(c: Ctl): Set<string> {
  const p = c.agent.data;
  const out = new Set<string>();
  if (!p || !p.visible) return out;
  for (const mid of p.assessment?.evidenceMediaIds ?? []) {
    const a = c.ev.alarms.find((x) => x.snapshotUrl?.endsWith(`/${mid}`));
    if (a) out.add(a.id);
  }
  return out;
}

const OP_ICON: Record<string, IconName> = { call: 'phone', dispatch: 'shield', note: 'note', bookmark: 'bookmark', clip_export: 'download' };
const OP_WORD: Record<string, string> = { call: 'Call', dispatch: 'Dispatch', note: 'Note', bookmark: 'Bookmark', clip_export: 'Clip export' };
const HUMAN = new Set(['operator', 'supervisor', 'admin']);

function buildLanes(c: Ctl, cited: Set<string>, now: number): Lane[] {
  const ev = c.ev;
  const tz = ev.siteTimeZone;
  const rank = new Map(c.railCams.slice(0, 9).map((x, i) => [x.id, i + 1]));
  const camById = new Map(ev.cameras.map((x) => [x.id, x]));
  const lanes: Lane[] = [];
  const byKey = new Map<string, Lane>();
  const laneFor = (camId: string | null, name: string | null): Lane => {
    const k = camId ?? '__unmapped';
    let lane = byKey.get(k);
    if (!lane) {
      const full = (camId ? camById.get(camId)?.name : null) ?? name ?? 'Unmapped camera';
      lane = { key: k, kind: 'cam', label: camShort(full), sub: full.split(' · ').slice(1).join(' · '), camId, keyNo: camId ? (rank.get(camId) ?? null) : null, marks: [] };
      byKey.set(k, lane);
      lanes.push(lane);
    }
    return lane;
  };

  ev.alarms.forEach((a, i) => {
    const isNew = c.newIds.has(a.id);
    const gt = isGuardTour(a);
    const ai = cited.has(a.id);
    laneFor(a.cameraId, a.cameraName).marks.push({
      key: `a-${a.id}`,
      t: alarmMs(a),
      kind: 'alarm',
      icon: gt ? 'route' : analyticIcon(a.analytic),
      cls: `st-${a.reviewStatus}${i === 0 ? ' first' : ''}${isNew ? ' fresh' : ''}${gt ? ' gt' : ''}${ai ? ' cited' : ''}`,
      label: `A${i + 1} · ${fmtTime(alarmMs(a), tz)} · ${camShort(a.cameraName)} · ${human(a.analytic, 'Alarm')}${a.burstCount > 1 ? ` ×${a.burstCount}` : ''} · ${alarmStatusWord(a)}${isNew ? ' · new' : ''}${gt ? ' · guard tour' : ''}${ai ? ' · AI evidence' : ''}`,
      alarm: a,
      n: i + 1,
      activate: () => c.selectAlarm(a.id),
    });
  });

  const aiLane: Lane = { key: '__ai', kind: 'ai', label: 'AI', sub: 'agent and watches', camId: null, keyNo: null, marks: [] };
  const opLane: Lane = { key: '__op', kind: 'op', label: 'Operator', sub: c.features.deterrence ? 'calls, dispatch, notes, talk-downs, deterrents' : 'calls, dispatch, notes', camId: null, keyNo: null, marks: [] };

  for (const w of c.watches ?? []) {
    const set = ms(w.createdAt);
    if (set !== null) {
      aiLane.marks.push({
        key: `ws-${w.id}`,
        t: set,
        kind: 'watch',
        icon: 'eye',
        cls: '',
        label: `Watch set${w.byAgent ? ' by the AI' : ''}: ${watchWhat(w)} on ${camList(w.cameraNames)} · ${fmtTime(set, tz)}`,
        watch: w,
        activate: () => c.seekTo(set),
      });
    }
    const hit = w.status === 'hit' ? ms(w.hitAt) : null;
    if (hit !== null) {
      const camId = w.hitCameraId;
      const lane = camId && (byKey.has(camId) || camById.has(camId)) ? laneFor(camId, null) : aiLane;
      const camName = camId ? (w.cameraNames[w.cameraIds.indexOf(camId)] ?? camById.get(camId)?.name ?? null) : null;
      lane.marks.push({
        key: `wh-${w.id}`,
        t: hit,
        kind: 'hit',
        icon: 'eye',
        cls: '',
        label: `AI watch saw ${watchWhat(w)}${camName ? ` on ${camShort(camName)}` : ''}${w.hitConfidence !== null ? ` (${pctOf(w.hitConfidence)})` : ''} · ${fmtTime(hit, tz)}`,
        watch: w,
        activate: () => {
          if (camId) c.selectCamera(camId);
          c.seekTo(hit - 5000);
        },
      });
    }
  }

  const p = c.agent.data;
  if (p && p.visible) {
    for (const call of p.toolCalls) {
      if (call.decision === 'deny' || call.status === 'failed') continue;
      const t = ms(call.at);
      if (t === null) continue;
      const would = call.decision === 'shadow';
      const line = toolResultLine(call);
      if (call.tool === 'record_assessment') {
        const v = typeof call.args?.verdict === 'string' ? call.args.verdict : null;
        aiLane.marks.push({ key: `tc-${call.id}`, t, kind: 'assess', icon: 'spark', cls: `s-${verdictCls(v)}${would ? ' would' : ''}`, label: `AI assessment${would ? ' (shadow)' : ''}: ${line ?? verdictLabel(v)} · ${fmtTime(t, tz)}`, activate: () => c.seekTo(t) });
      } else if (call.tool === 'escalate') {
        aiLane.marks.push({ key: `tc-${call.id}`, t, kind: 'escalate', icon: 'send', cls: would ? 'would' : '', label: `${would ? 'Would have handed' : 'Handed'} the event to an operator${line ? `: ${line}` : ''} · ${fmtTime(t, tz)}`, activate: () => c.seekTo(t) });
      } else if (call.tool === 'resolve_event') {
        aiLane.marks.push({ key: `tc-${call.id}`, t, kind: 'resolve', icon: 'check', cls: would ? 'would' : '', label: `${call.args?.kind === 'end_to_end' ? callWords(call) : would ? 'Would have auto-resolved' : 'Auto-resolved'}${line ? `: ${line}` : ''} · ${fmtTime(t, tz)}`, activate: () => c.seekTo(t) });
      } else if (would && (call.tool === 'talk_down' || call.tool === 'trigger_relay' || call.tool === 'notify_customer')) {
        // Done device calls are actions (below); in shadow only the would-have is left.
        const wl = toolResultLine(call, c.devices.data ? c.devices.names : null);
        aiLane.marks.push({ key: `tc-${call.id}`, t, kind: 'deter', icon: call.tool === 'talk_down' ? 'speaker' : call.tool === 'notify_customer' ? 'mail' : 'bolt', cls: 'would', label: `${callWords(call)}${wl ? `: ${wl}` : ''} · ${fmtTime(t, tz)}`, activate: () => c.seekTo(t) });
      }
    }
  }

  for (const x of ev.actions) {
    if (!HUMAN.has(x.actor.type) || !OP_ICON[x.type]) continue;
    const pAt = x.params.at;
    const t = (typeof pAt === 'string' ? ms(pAt) : null) ?? ms(x.startedAt);
    if (t === null) continue;
    const camId = typeof x.params.cameraId === 'string' ? x.params.cameraId : null;
    const extra = x.type === 'bookmark' && camId ? ` · ${camShort(camById.get(camId)?.name)}` : x.status !== 'succeeded' ? ` · ${x.status}` : '';
    opLane.marks.push({
      key: `ac-${x.id}`,
      t,
      kind: 'op',
      icon: OP_ICON[x.type] ?? 'user',
      cls: `op-${x.type}`,
      label: `${OP_WORD[x.type] ?? human(x.type)} · ${actorName(x.actor)} · ${fmtTime(t, tz)}${extra}`,
      activate: () => {
        if (camId) c.selectCamera(camId);
        c.seekTo(t);
      },
    });
  }
  // Phase 5: talk-downs and relay actions, on the row of whoever did them.
  for (const x of ev.actions) {
    if (x.type !== 'talk_down' && x.type !== 'relay') continue;
    const t = ms(x.startedAt);
    if (t === null) continue;
    const byAi = x.actor.type === 'agent';
    if (!byAi && !HUMAN.has(x.actor.type)) continue;
    const p = x.params;
    const ids = Array.isArray(p.deviceIds) ? p.deviceIds.filter((v): v is string => typeof v === 'string') : [];
    const where = ids.map((id) => c.devices.names.zone(id) ?? id).join(', ');
    const label =
      x.type === 'talk_down'
        ? `${byAi ? 'AI talk-down' : 'Talk-down'}${where ? ` on ${where}` : ''}: ${p.live === true ? 'push-to-talk' : str(p.messageName) ? `“${str(p.messageName)}”` : 'typed message'}`
        : `${byAi ? 'AI · ' : ''}${str(p.deviceName) ?? 'Relay'} ${p.action === 'pulse' ? `pulsed ${typeof p.seconds === 'number' ? `${p.seconds} s` : ''}` : p.action === 'hold' ? 'on (hold)' : 'released'}`;
    (byAi ? aiLane : opLane).marks.push({
      key: `dv-${x.id}`,
      t,
      kind: 'deter',
      icon: x.type === 'talk_down' ? (p.live === true ? 'mic' : 'speaker') : functionIcon(str(p.function)),
      cls: `dv-${x.type}${x.status !== 'succeeded' ? ' failed' : ''}`,
      label: `${label} · ${byAi ? 'AI agent' : actorName(x.actor)} · ${fmtTime(t, tz)}${x.status !== 'succeeded' ? ` · ${x.status}` : ''}`,
      action: x,
      activate: () => c.seekTo(Math.min(t - 3000, now - 2000)),
    });
  }
  for (const l of ev.log) {
    if (l.kind === 'customer_notified' || l.kind === 'customer_report_sent') {
      const t = ms(l.at);
      if (t === null) continue;
      (l.actor.type === 'agent' ? aiLane : opLane).marks.push({ key: `lg-${l.seq}`, t, kind: 'op', icon: 'mail', cls: 'op-notify', label: `${l.text} · ${fmtTime(t, tz)}`, activate: () => c.setCtx('contacts') });
      continue;
    }
    if (l.kind !== 'parked' && l.kind !== 'raised' && l.kind !== 'raise_escalated') continue;
    const t = ms(l.at);
    if (t === null) continue;
    opLane.marks.push({ key: `lg-${l.seq}`, t, kind: 'op', icon: l.kind === 'parked' ? 'park' : 'up', cls: `op-${l.kind}`, label: `${l.text} · ${fmtTime(t, tz)}`, activate: () => c.seekTo(Math.min(t, now - 2000)) });
  }

  for (const lane of lanes) lane.marks.sort((x, y) => x.t - y.t);
  aiLane.marks.sort((x, y) => x.t - y.t);
  opLane.marks.sort((x, y) => x.t - y.t);
  return [...lanes, ...(aiLane.marks.length ? [aiLane] : []), ...(opLane.marks.length ? [opLane] : [])];
}

function clusterLane(marks: Mk[], thrMs: number): Item[] {
  const out: Item[] = [];
  let group: Mk[] = [];
  const flush = () => {
    if (group.length === 1) out.push({ k: 'mk', key: group[0]!.key, t: group[0]!.t, mk: group[0]! });
    else if (group.length > 1) {
      const t0 = group[0]!.t;
      const t1 = group[group.length - 1]!.t;
      out.push({ k: 'cl', key: `cl-${group[0]!.key}`, t: (t0 + t1) / 2, t0, t1, marks: group });
    }
    group = [];
  };
  for (const m of marks) {
    const prev = group[group.length - 1];
    if (prev && m.t - prev.t >= thrMs) flush();
    group.push(m);
  }
  flush();
  return out;
}

/* ---------- hover / focus cards ---------- */

interface CardProps {
  c: Ctl;
  item: Item;
  rect: DOMRect;
  onSplit: (alarmId: string) => void;
  onZoom: (it: Extract<Item, { k: 'cl' }>) => void;
  keep: () => void;
  hideSoon: () => void;
  close: () => void;
  cardRef: (el: HTMLDivElement | null) => void;
}

function cardPos(rect: DOMRect): { left: number; bottom: number } {
  const w = 270;
  const left = Math.max(8, Math.min(window.innerWidth - w - 8, rect.left + rect.width / 2 - w / 2));
  return { left, bottom: Math.max(8, window.innerHeight - rect.top + 8) };
}

function AlarmCard({ c, a, n, onSplit }: { c: Ctl; a: EventAlarm; n: number; onSplit: (id: string) => void }) {
  const ev = c.ev;
  const tz = ev.siteTimeZone;
  const t = alarmMs(a);
  const isFirst = a.id === ev.alarms[0]?.id;
  const cited = citedAlarmIds(c).has(a.id);
  const restore = a.reviewStatus === 'dismissed';
  return (
    <>
      <div className="tsnap">
        <AuthImg url={a.snapshotUrl} alt={`Snapshot: ${a.cameraName ?? 'camera'} at ${fmtTime(t, tz)}`} fallback="No snapshot" />
      </div>
      <div className="tch">
        <b className="mono">A{n}</b>
        <span className="mono">
          {fmtTime(t, tz)} {tzAbbr(tz, t)}
        </span>
        <span className={`tst st-${a.reviewStatus}`}>{alarmStatusWord(a)}</span>
        {c.newIds.has(a.id) && <span className="nb">NEW</span>}
      </div>
      <div className="tcam">{a.cameraName ?? 'Unmapped camera'}</div>
      <div className="tmeta">
        {isGuardTour(a) && <GuardTourTag />}
        {human(a.analytic, 'Alarm')}
        {a.burstCount > 1 && <span className="dim"> · burst ×{a.burstCount}</span>}
        {cited && (
          <span className="tagx ai" title="The agent cited this snapshot in its assessment">
            AI evidence
          </span>
        )}
      </div>
      <div className="row-wrap">
        <button type="button" className="btn sm pri" onClick={() => c.selectAlarm(a.id)}>
          <Icon name="play" />
          Load clip
        </button>
        <button
          type="button"
          className="btn sm"
          onClick={() => onSplit(a.id)}
          disabled={!c.canAct || isFirst || ev.alarms.length < 2}
          title={isFirst ? 'The first alarm opened this event' : !c.canAct ? 'Only the owner can split' : `Move A${n} into its own event`}
        >
          <Icon name="split" />
          Split
        </button>
        <button
          type="button"
          className="btn sm"
          disabled={!c.canAct || !!c.busy}
          onClick={() => void c.run(restore ? 'Restore alarm' : 'Dismiss alarm', (v) => api.reviewAlarm(ev.id, a.id, restore ? 'reviewed' : 'dismissed', v), (d) => d)}
          title={restore ? 'Mark this alarm reviewed again' : 'Dismiss this alarm (false alarm or noise)'}
        >
          {restore ? 'Restore' : 'Dismiss'}
        </button>
      </div>
    </>
  );
}

function HitCard({ c, w }: { c: Ctl; w: Watch }) {
  const tz = c.ev.siteTimeZone;
  const cam = w.hitCameraId ? (w.cameraNames[w.cameraIds.indexOf(w.hitCameraId)] ?? null) : null;
  return (
    <>
      <div className="tsnap ai">
        <AuthImg url={w.frameUrl} alt={`Frame: ${watchWhat(w)}${cam ? ` on ${cam}` : ''}`} fallback="No frame" />
      </div>
      <div className="tch ai-t">
        <Icon name="eye" />
        <b>AI watch hit</b>
        {w.hitAt && (
          <span className="mono">
            {fmtTime(w.hitAt, tz)} {tzAbbr(tz, w.hitAt)}
          </span>
        )}
      </div>
      <div className="tcam">
        Saw {watchWhat(w)}
        {cam ? ` on ${cam}` : ''}
        {w.hitConfidence !== null ? ` (${pctOf(w.hitConfidence)})` : ''}
      </div>
      <div className="tmeta">{hitOutcome(c.ev.log, w).raised ? 'It raised the event back to its owner.' : w.byAgent ? 'Set by the AI.' : 'Set by an operator.'}</div>
    </>
  );
}

/** Phase 5: a talk-down or relay action: who, where, what was played (and its audio) or for how long. */
function DeterCard({ c, a }: { c: Ctl; a: EventAction }) {
  const tz = c.ev.siteTimeZone;
  const p = a.params;
  const byAi = a.actor.type === 'agent';
  const t = ms(a.startedAt) ?? 0;
  const ids = Array.isArray(p.deviceIds) ? p.deviceIds.filter((v): v is string => typeof v === 'string') : [];
  const r = a.result ?? {};
  const sim = r.simulated === true || (Array.isArray(r.devices) && (r.devices as { simulated?: boolean }[]).some((d) => d.simulated));
  return (
    <>
      <div className={`tch${byAi ? ' ai-t' : ''}`}>
        <Icon name={a.type === 'talk_down' ? 'speaker' : functionIcon(str(p.function))} />
        <b>{a.type === 'talk_down' ? (p.live === true ? 'Push-to-talk' : 'Talk-down') : human(str(p.function), 'Relay')}</b>
        <span className="mono">
          {fmtTime(t, tz)} {tzAbbr(tz, t)}
        </span>
        {a.status !== 'succeeded' && <span className="tst st-new">{a.status}</span>}
      </div>
      <div className="tcam">
        {a.type === 'talk_down' ? `On ${ids.map((id) => c.devices.names.zone(id) ?? id).join(', ') || 'a speaker'}` : (str(p.deviceName) ?? 'Relay')}
        {a.type === 'relay' && ` · ${p.action === 'pulse' ? `pulse ${typeof p.seconds === 'number' ? `${p.seconds} s` : ''}` : p.action === 'hold' ? 'hold' : 'release'}`}
      </div>
      {a.type === 'talk_down' && (str(p.messageName) || str(p.text)) && (
        <div className="tmeta">
          {str(p.messageName) && <b>{str(p.messageName)} · </b>}
          {str(p.text) ? `“${str(p.text)}”` : ''}
        </div>
      )}
      <div className="tmeta">
        {byAi ? 'By the AI agent' : `By ${actorName(a.actor)}`}
        {typeof r.durationMs === 'number' ? ` · ${(r.durationMs / 1000).toFixed(1)} s` : ''}
        {sim ? ' · simulated' : ''}
      </div>
      {a.type === 'talk_down' && a.status === 'succeeded' && <AudioButton url={api.talkdownAudioUrl(a.id)} label="Play the audio" small />}
    </>
  );
}

function Card({ c, item, rect, onSplit, onZoom, keep, hideSoon, close, cardRef }: CardProps) {
  const pos = cardPos(rect);
  const tz = c.ev.siteTimeZone;
  let body: ReactNode = null;
  if (item.k === 'mk' && item.mk.alarm) body = <AlarmCard c={c} a={item.mk.alarm} n={item.mk.n ?? 0} onSplit={onSplit} />;
  else if (item.k === 'mk' && item.mk.kind === 'hit' && item.mk.watch) body = <HitCard c={c} w={item.mk.watch} />;
  else if (item.k === 'mk' && item.mk.kind === 'deter' && item.mk.action) body = <DeterCard c={c} a={item.mk.action} />;
  else if (item.k === 'cl') {
    body = (
      <>
        <div className="tch">
          <b>{plural(item.marks.length, 'marker')}</b>
          <span className="mono">
            {fmtTime(item.t0, tz)}–{fmtTime(item.t1, tz)} {tzAbbr(tz, item.t0)}
          </span>
        </div>
        <div className="tlist" role="list">
          {item.marks.slice(0, 14).map((m) => (
            <button key={m.key} type="button" role="listitem" className={`tli k-${m.kind} ${m.cls}`} onClick={() => (m.activate(), close())}>
              <Icon name={m.icon} />
              <span>{m.label}</span>
            </button>
          ))}
          {item.marks.length > 14 && <span className="note">+{item.marks.length - 14} more: zoom in to see them.</span>}
        </div>
        <button type="button" className="btn sm" onClick={() => onZoom(item)}>
          <Icon name="search" />
          Zoom in here
        </button>
      </>
    );
  }
  if (!body) return null;
  return (
    <div
      ref={cardRef}
      className="tcard"
      style={{ left: pos.left, bottom: pos.bottom }}
      role="group"
      aria-label="Marker details"
      onMouseEnter={keep}
      onMouseLeave={hideSoon}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), close())}
    >
      {body}
    </div>
  );
}

/* ---------- timeline ---------- */

export function Timeline({ c, onSplit }: { c: Ctl; onSplit: (alarmId: string) => void }) {
  const ev = c.ev;
  const tz = ev.siteTimeZone;
  const now = useNow(1000);
  const cited = useMemo(() => citedAlarmIds(c), [c.agent.data, ev]); // eslint-disable-line react-hooks/exhaustive-deps
  const lanes = buildLanes(c, cited, now);

  const first = ms(ev.firstAlarmAt) ?? (ev.alarms[0] ? alarmMs(ev.alarms[0]) : now);
  const lastA = ms(ev.lastAlarmAt) ?? first;
  const closed = ev.state === 'closed' || ev.state === 'dispositioned';
  const lastMark = Math.max(lastA, ...lanes.flatMap((l) => l.marks.map((m) => m.t)));
  const a = first - 5 * 60_000;
  const b = closed ? Math.max(ms(ev.closedAt) ?? lastMark, lastMark) + 60_000 : Math.max(now, lastA + 60_000);
  const span = Math.max(1, b - a);
  const pct = (t: number) => Math.max(0, Math.min(100, ((t - a) / span) * 100));
  const sel = selectedAlarm(ev, c.view);
  const playhead = vtime(c.view, now);
  const unrev = ev.alarms.filter((x) => x.reviewStatus === 'new').length;
  const camIds = lanes.flatMap((l) => (l.camId ? [l.camId] : []));
  const footage = useFootage(camIds, a, b, now, !closed);
  const anyFootage = [...footage.values()].some((p) => p !== null);

  /* zoom and size */
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const labelsRef = useRef<HTMLDivElement | null>(null);
  const [trackW, setTrackW] = useState(800);
  const [zoom, setZoom] = useState(1);
  const [scrollX, setScrollX] = useState(0);
  const zmax = Math.max(1, span / MIN_SPAN_MS);
  const z = Math.min(zoom, zmax);
  const innerW = trackW * z;
  const pendingScroll = useRef<number | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setTrackW(Math.max(200, el.clientWidth)));
    ro.observe(el);
    setTrackW(Math.max(200, el.clientWidth));
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && pendingScroll.current !== null) {
      el.scrollLeft = Math.max(0, pendingScroll.current);
      pendingScroll.current = null;
      setScrollX(el.scrollLeft);
    }
  }, [z]);
  const zoomTo = (nz: number, anchorX?: number) => {
    const el = scrollRef.current;
    const target = Math.max(1, Math.min(zmax, nz));
    if (!el || target === z) return;
    const ax = anchorX ?? el.clientWidth / 2;
    const frac = (el.scrollLeft + ax) / Math.max(1, el.scrollWidth);
    pendingScroll.current = frac * el.clientWidth * target - ax;
    setZoom(target);
  };
  const zoomOnto = (t0: number, t1: number) => {
    const el = scrollRef.current;
    if (!el) return false;
    const target = Math.max(1, Math.min(zmax, span / (Math.max(t1 - t0, 2000) * 4)));
    if (target <= z * 1.05) return false;
    const w = el.clientWidth * target;
    pendingScroll.current = (((t0 + t1) / 2 - a) / span) * w - el.clientWidth / 2;
    setZoom(target);
    return true;
  };
  const zoomRef = useRef(zoomTo);
  zoomRef.current = zoomTo;
  const zoomStateRef = useRef(z);
  zoomStateRef.current = z;
  // Ctrl/⌘-wheel zooms around the pointer (a non-passive listener, so the page does not zoom).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const on = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const f = Math.exp(-e.deltaY * 0.002);
      zoomRef.current(zoomStateRef.current * f, e.clientX - r.left);
    };
    el.addEventListener('wheel', on, { passive: false });
    return () => el.removeEventListener('wheel', on);
  }, []);

  /* items (clusters) per lane */
  const thrMs = (CLUSTER_PX / Math.max(1, innerW)) * span;
  const laneItems = lanes.map((l) => ({ lane: l, items: clusterLane(l.marks, thrMs) }));
  const itemByKey = new Map<string, Item>();
  for (const { items } of laneItems) for (const it of items) itemByKey.set(it.key, it);
  const itemOfAlarm = (id: string | undefined) => {
    if (!id) return null;
    for (const { items } of laneItems) for (const it of items) if ((it.k === 'mk' && it.mk.alarm?.id === id) || (it.k === 'cl' && it.marks.some((m) => m.alarm?.id === id))) return it;
    return null;
  };

  /* cards */
  const [card, setCard] = useState<{ key: string; rect: DOMRect } | null>(null);
  const hideT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardEl = useRef<HTMLDivElement | null>(null);
  const keep = () => {
    if (hideT.current) clearTimeout(hideT.current);
    hideT.current = null;
  };
  const hideSoon = () => {
    keep();
    hideT.current = setTimeout(() => setCard(null), 220);
  };
  useEffect(() => () => keep(), []);
  const show = (key: string, el: HTMLElement) => {
    keep();
    setCard({ key, rect: el.getBoundingClientRect() });
  };
  const cardItem = card ? itemByKey.get(card.key) : undefined;

  /* focus follows the selection (J, a click on a marker or a toast) when focus is on the page or the timeline */
  const prevSel = useRef(sel?.id);
  useEffect(() => {
    if (prevSel.current === sel?.id) return;
    prevSel.current = sel?.id;
    const root = rootRef.current;
    const act = document.activeElement;
    if (!root || !(act === document.body || act === null || root.contains(act))) return;
    const it = itemOfAlarm(sel?.id);
    const el = it ? root.querySelector<HTMLElement>(`[data-mk="${CSS.escape(it.key)}"]`) : null;
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  });

  const focusItem = (key: string | undefined) => {
    if (!key) return;
    rootRef.current?.querySelector<HTMLElement>(`[data-mk="${CSS.escape(key)}"]`)?.focus();
  };
  const onMarkKey = (e: KeyboardEvent<HTMLButtonElement>, li: number, ii: number) => {
    const items = laneItems[li]?.items ?? [];
    const it = items[ii];
    if (!it) return;
    let next: string | undefined;
    if (e.key === 'ArrowLeft') next = items[ii - 1]?.key;
    else if (e.key === 'ArrowRight') next = items[ii + 1]?.key;
    else if (e.key === 'Home') next = items[0]?.key;
    else if (e.key === 'End') next = items[items.length - 1]?.key;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const other = laneItems[li + (e.key === 'ArrowUp' ? -1 : 1)]?.items ?? [];
      next = other.slice().sort((x, y) => Math.abs(x.t - it.t) - Math.abs(y.t - it.t))[0]?.key;
    } else if (e.key === 'Escape' && card) {
      e.stopPropagation();
      setCard(null);
      return;
    } else return;
    e.preventDefault();
    e.stopPropagation();
    focusItem(next);
  };

  /* seeking */
  const timeAt = (clientX: number) => {
    const el = scrollRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return a + ((el.scrollLeft + clientX - r.left) / Math.max(1, el.scrollWidth)) * span;
  };
  const seekLane = (e: MouseEvent<HTMLDivElement>, camId: string | null) => {
    const t = timeAt(e.clientX);
    if (t === null) return;
    if (camId && camId !== c.view.cameraId) c.selectCamera(camId);
    c.seekTo(t);
  };

  /* scrub preview: a small frame from the moment under the pointer on a camera lane (not over a marker) */
  const [preview, setPreview] = useState<{ camId: string; camName: string; t: number; x: number; y: number; recorded: boolean } | null>(null);
  const previewT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropPreview = () => {
    if (previewT.current) clearTimeout(previewT.current);
    previewT.current = null;
    setPreview(null);
  };
  useEffect(() => () => dropPreview(), []);
  const hoverLane = (e: MouseEvent<HTMLDivElement>, lane: Lane) => {
    const periods = lane.camId ? footage.get(lane.camId) : null;
    const t = timeAt(e.clientX);
    // Only lanes whose camera has an archive; the live edge has no frame yet; markers have their own cards.
    if (!lane.camId || !periods || t === null || t > Date.now() - 3000 || (e.target as HTMLElement).closest('.tmk')) return dropPreview();
    const row = e.currentTarget.getBoundingClientRect();
    const next = { camId: lane.camId, camName: lane.label, t: Math.round(t / 1000) * 1000, x: e.clientX, y: row.top, recorded: recordedAt(periods, t) };
    if (previewT.current) clearTimeout(previewT.current);
    previewT.current = setTimeout(() => setPreview(next), PREVIEW_DELAY_MS);
  };
  const onRulerKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      c.seekTo(playhead + (e.key === 'ArrowLeft' ? -10_000 : 10_000));
    }
  };

  /* ruler ticks (only the visible stretch is drawn) */
  const step = TICK_STEPS.find((s) => (s / span) * innerW >= 90) ?? TICK_STEPS[TICK_STEPS.length - 1]!;
  const vis0 = a + ((scrollX - 100) / innerW) * span;
  const vis1 = a + ((scrollX + trackW + 100) / innerW) * span;
  const ticks: number[] = [];
  for (let t = Math.ceil(Math.max(a, vis0) / step) * step; t <= Math.min(b, vis1) && ticks.length < 200; t += step) ticks.push(t);

  const tabKeyOf = (items: Item[]) => {
    const s = items.find((it) => (it.k === 'mk' ? it.mk.alarm?.id === sel?.id : it.marks.some((m) => m.alarm?.id === sel?.id)));
    return (s ?? items[items.length - 1])?.key;
  };

  const renderItem = (it: Item, li: number, ii: number, tabKey: string | undefined) => {
    const isOn = it.k === 'mk' ? !!it.mk.alarm && it.mk.alarm.id === sel?.id : it.marks.some((m) => m.alarm?.id === sel?.id);
    const common = {
      'data-mk': it.key,
      tabIndex: it.key === tabKey ? 0 : -1,
      style: { left: `${pct(it.t)}%` },
      onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => onMarkKey(e, li, ii),
      onMouseEnter: (e: MouseEvent<HTMLButtonElement>) => show(it.key, e.currentTarget),
      onMouseLeave: hideSoon,
      onFocus: (e: FocusEvent<HTMLButtonElement>) => show(it.key, e.currentTarget),
      onBlur: (e: FocusEvent<HTMLButtonElement>) => {
        if (cardEl.current && e.relatedTarget instanceof Node && cardEl.current.contains(e.relatedTarget)) return;
        hideSoon();
      },
    };
    let btn: ReactNode;
    if (it.k === 'mk') {
      const m = it.mk;
      const burst = m.alarm && m.alarm.burstCount > 1 ? m.alarm.burstCount : 0;
      btn = (
        <button
          type="button"
          {...common}
          className={`tmk k-${m.kind} ${m.cls}${isOn ? ' on' : ''}`}
          aria-label={m.label}
          aria-pressed={m.alarm ? isOn : undefined}
          title={m.alarm || m.kind === 'hit' || m.action ? undefined : m.label}
          onClick={(e) => {
            e.stopPropagation();
            m.activate();
          }}
        >
          <Icon name={m.icon} />
          {burst > 0 && <span className="bn">×{burst}</span>}
        </button>
      );
    } else {
      const unr = it.marks.filter((m) => m.alarm?.reviewStatus === 'new').length;
      const fresh = it.marks.some((m) => m.alarm && c.newIds.has(m.alarm.id));
      btn = (
        <button
          type="button"
          {...common}
          className={`tmk tcl${unr ? ' st-new' : ''}${fresh ? ' fresh' : ''}${isOn ? ' on' : ''}`}
          aria-label={`${plural(it.marks.length, 'marker')} from ${fmtTime(it.t0, tz)} to ${fmtTime(it.t1, tz)}${unr ? `, ${unr} unreviewed` : ''}. Enter zooms in.`}
          onClick={(e) => {
            e.stopPropagation();
            if (!zoomOnto(it.t0, it.t1)) show(it.key, e.currentTarget);
          }}
        >
          {it.marks.length}
        </button>
      );
    }
    return (
      <Fragment key={it.key}>
        {btn}
        {card?.key === it.key && cardItem && (
          <Card
            c={c}
            item={cardItem}
            rect={card.rect}
            onSplit={(id) => {
              setCard(null);
              onSplit(id);
            }}
            onZoom={(cl) => {
              setCard(null);
              zoomOnto(cl.t0, cl.t1);
            }}
            keep={keep}
            hideSoon={hideSoon}
            close={() => setCard(null)}
            cardRef={(el) => (cardEl.current = el)}
          />
        )}
      </Fragment>
    );
  };

  const nowIn = !closed && now >= a && now <= b;

  return (
    <div className="tline lanes" ref={rootRef}>
      <div className="tl-top">
        <span className="lbl">
          Timeline · {tzAbbr(tz)} site time · {plural(ev.alarmCount, 'alarm')}
          {unrev > 0 && <span className="hot"> · {unrev} unreviewed</span>}
        </span>
        <button type="button" className="btn sm" onClick={c.nextNew} disabled={!unrev} title="Load the next unreviewed alarm">
          Next new <kbd>J</kbd>
        </button>
        <span className="mapkey tl-key" aria-hidden="true">
          <span>
            <i className="k-new" />
            unreviewed
          </span>
          <span>
            <i className="k-rev" />
            reviewed
          </span>
          <span>
            <i className="k-dis" />
            dismissed
          </span>
          <span>
            <i className="k-ai" />
            AI
          </span>
          <span>
            <i className="k-op" />
            operator
          </span>
          {anyFootage && (
            <span>
              <i className="k-rec" />
              recorded
            </span>
          )}
        </span>
        <span className="sp" />
        <span className="tl-zoom" role="group" aria-label="Timeline zoom">
          <button type="button" className="btn sm icon" onClick={() => zoomTo(z / 2)} disabled={z <= 1} aria-label="Zoom out" title="Zoom out (Ctrl/⌘ + wheel)">
            <Icon name="minus" />
          </button>
          <span className="mono dim">{z < 10 ? z.toFixed(1) : Math.round(z)}×</span>
          <button type="button" className="btn sm icon" onClick={() => zoomTo(z * 2)} disabled={z >= zmax} aria-label="Zoom in" title="Zoom in (Ctrl/⌘ + wheel)">
            <Icon name="plus" />
          </button>
          <button type="button" className="btn sm" onClick={() => zoomTo(1)} disabled={z <= 1} title="Show the whole event">
            <Icon name="fit" />
            Fit all
          </button>
        </span>
      </div>
      <div className="tl-body">
        <div className="tl-labels" ref={labelsRef} aria-hidden="true">
          <div className="tl-lab ruler-lab" />
          {lanes.map((l) => (
            <div key={l.key} className={`tl-lab k-${l.kind}${l.camId && l.camId === c.view.cameraId ? ' on' : ''}`} title={l.camId ? `${l.label}${l.sub ? ` · ${l.sub}` : ''} · click to show it` : l.sub}>
              {l.camId ? (
                <button type="button" tabIndex={-1} onClick={() => c.selectCamera(l.camId!)}>
                  <b>{l.label}</b>
                  {l.sub && <span className="dim"> {l.sub}</span>}
                </button>
              ) : (
                <b>{l.label}</b>
              )}
              {l.keyNo !== null && <kbd>{l.keyNo}</kbd>}
            </div>
          ))}
        </div>
        <div
          className="tl-scroll"
          ref={scrollRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (labelsRef.current) labelsRef.current.scrollTop = el.scrollTop;
            setScrollX(el.scrollLeft);
            // Keep an open card on its marker (focus can scroll the lanes), or drop it once the marker is out of view.
            if (card) {
              const m = el.querySelector<HTMLElement>(`[data-mk="${CSS.escape(card.key)}"]`);
              const r = m?.getBoundingClientRect();
              const box = el.getBoundingClientRect();
              if (!m || !r || r.right < box.left || r.left > box.right || r.bottom < box.top || r.top > box.bottom) setCard(null);
              else setCard({ key: card.key, rect: r });
            }
          }}
        >
          <div className="tl-inner" style={{ width: z > 1 ? `${innerW}px` : '100%' }}>
            <div
              className="tl-ruler"
              onClick={(e) => {
                const t = timeAt(e.clientX);
                if (t !== null) c.seekTo(t);
              }}
              onKeyDown={onRulerKey}
              role="slider"
              tabIndex={0}
              aria-label="Event timeline. Click or use the arrow keys to review archive."
              aria-valuemin={a}
              aria-valuemax={b}
              aria-valuenow={Math.round(playhead)}
              aria-valuetext={`${fmtTime(playhead, tz)} ${tzAbbr(tz)}`}
            >
              {ticks.map((t) => (
                <span key={t} className="tk" style={{ left: `${pct(t)}%` }}>
                  {fmtTime(t, tz, step < 60_000)}
                </span>
              ))}
              <span className="tk first" style={{ left: `${pct(first)}%` }} title={`First alarm · ${fmtTime(first, tz)}`} />
            </div>
            {laneItems.map(({ lane, items }, li) => {
              const tabKey = tabKeyOf(items);
              const periods = lane.camId ? footage.get(lane.camId) : null;
              return (
                <div
                  key={lane.key}
                  className={`tl-lane k-${lane.kind}${lane.camId && lane.camId === c.view.cameraId ? ' on' : ''}${periods ? ' fp' : ''}`}
                  role="group"
                  aria-label={`${lane.label} lane${lane.sub ? ` (${lane.sub})` : ''}: ${plural(lane.marks.length, 'marker')}${periods ? `, ${periods.length ? `video recorded in ${plural(periods.length, 'stretch', 'stretches')}` : 'no recorded video'}` : ''}`}
                  onClick={(e) => seekLane(e, lane.camId)}
                  onMouseMove={(e) => hoverLane(e, lane)}
                  onMouseLeave={dropPreview}
                >
                  {periods?.map((p) => (
                    <span key={p.start} className="tl-rec" style={{ left: `${pct(p.start)}%`, width: `${Math.max(0.15, pct(p.end) - pct(p.start))}%` }} aria-hidden="true" />
                  ))}
                  {items.map((it, ii) => renderItem(it, li, ii, tabKey))}
                </div>
              );
            })}
            <span className="tl-first" style={{ left: `${pct(first)}%` }} aria-hidden="true" />
            {nowIn && <span className="tl-now" style={{ left: `${pct(now)}%` }} aria-hidden="true" />}
            <span className="playhead" style={{ left: `${pct(playhead)}%` }} aria-hidden="true" />
          </div>
        </div>
      </div>
      {preview && (
        <div className="tl-preview" style={{ left: preview.x, top: preview.y }} aria-hidden="true">
          {preview.recorded ? <AuthImg url={api.previewUrl(preview.camId, new Date(preview.t).toISOString())} alt="" /> : <span className="media-ph">No recording</span>}
          <span className="tpt">
            {preview.camName} · {fmtTime(preview.t, tz, true)}
          </span>
        </div>
      )}
    </div>
  );
}

